import React, {Fragment, useEffect, useState} from "react";
import { IClientAccount } from '../../../interface/IClientAccount.ts';
import { IOption } from '../../../interface/IOption.ts';
import { useClientAccountApi } from '../../../features/auth/hooks/use-client-account-api.ts';
import { useListsApi } from "../../../features/auth/hooks/use-lists-api.ts";
import { useCertificatesApi } from "../../../features/auth/hooks/use-certifictates-api.ts";
import ApiCheck from '../../Structure/ApiCheck.tsx';
import ClientAccountForm from "../../Form/ClientAccountForm.tsx";
import SingleAccountPreview from "../../ClientAccounts/SingleAccountPreview.tsx";
import NavigateConfirmModal from "../../Nav/NavigateConfirmModal.tsx";
import { useBlocker, useLocation, useNavigate } from "react-router-dom";
import '../../../scss/components/BankConnection.scss';
import '../../../scss/components/Repeater.scss';
import ToggleIcon from "../../../img/inline/ToggleIcon.tsx";
import { CertSimplified, ICertificate } from "../../../interface/ICertificate.ts";
import { IProfile } from "../../../interface/IProfile.ts";
import { iso3166 } from "../../../static/iso3166.js";
import Button from "../../Form/Button.tsx";
import { ApiResponseType } from "../../../features/api/types.ts";
import { useHelper } from "../../../features/functions/helper.tsx";
import { useProfileContext } from "../../../features/auth/hooks/use-profile-context.ts";

const TabBankConnection = ({
    profile,
    addition
}:{
    profile:IProfile,
    addition:number
}) => {
    const { sortCertificateArray, isBankingCertificate, moveItem } = useHelper();
    // Applicants registered for the Zentralprüfung (not yet validated) get their own intro.
    const { needsInitialReview } = useProfileContext();
    
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const [responseMessage, setResponseMessage] = useState<string>('');
    const [responseMessageClass, setResponseMessageClass] = useState<string>('');

    const [certIsLoading, setCertIsLoading] = useState<boolean>(false);
    const {getClientAccounts} = useClientAccountApi();
    const [allClientAccounts, setAllClientAccounts] = useState<Array<IClientAccount>|any>([]);
    const [toggledStates, setToggledStates] = useState<Array<Number>|any>([]);
    const { getList } = useListsApi();
    const { getCertificates, updateCertificate } = useCertificatesApi();
    const [clientCertificates, setClientCertificates] = useState<Array<ICertificate>|any>([]);
    
    const [navigateConfirmModal, setNavigateConfirmModal] = useState(false);
    const [certsByAccountsWithUnsavedChanges, setCertsByAccountsWithUnsavedChanges] = useState<Object>({});
    const [accountsBeingSaved, setAccountsBeingSaved] = useState<Array<string|number>>([]);

    const [apiErrors, setApiErrors] = useState<any>();
    
    const [clientAccountTypePrivate, setClientAccountTypePrivate] = useState<IOption>();
    const [clientAccountTypeCompany, setClientAccountTypeCompany] = useState<IOption>();
    const [transferPayment, setTransferPayment] = useState<IOption>();
    const [directDebitPayment, setDirectDebitPayment] = useState<IOption>();
    const [selectedAccountType, setSelectedAccountType] = useState<number|undefined>();

    const [unSavedChanges, setUnSavedChanges] = useState<boolean>(false);

    const [hasCFP, setHasCFP] = useState<boolean>(false);
    const [hasCFEP, setHasCFEP] = useState<boolean>(false);

    const [hasEFA, setHasEFA] = useState<boolean>(false);
    const [hasCGA, setHasCGA] = useState<boolean>(false);

    const [hasEmeritus, setHasEmeritus] = useState<boolean>(false);

    const [showNewForm, setShowNewFom] = useState<boolean>(false);
    
    const { updateClientAccount } = useClientAccountApi();

    //A new empty ClientAccount
    const [emptyClient, setEmptyClient] = useState<IClientAccount>({
        id: null,
        client_account_billing_name: '',
        client_account_department: '',
        client_account_cost_position: '',
        client_account_budget_number: '',
        client_account_own_billing_address: '1',
        client_account_project_number: '',
        client_account_address: '',
        client_account_zip: '',
        client_account_city: '',
        client_account_country: '',
        client_account_email: '',
        client_account_ust_id: '',
        client_account_type: null,
        bank_account_owner: '',
        bank_account_name: '',
        bank_account_iban: '',
        bank_account_bic: '',
        bank_account_debit_authorization: false,
        update_allowed: true,
        assigned_certificates: [],
        client_account_payment_type: null,
        client_account_mandate_valid_from: '',
    });

    const handleApiError = (errorHint:string = '') => {
        setApiErrors({
            errors: `Der Server ist aktuell nicht erreichbar. Bitte probieren Sie es später erneut.${errorHint ? ' - ' + errorHint : ''}`
        });
        setIsLoading(false);
    }

    const loadCertificates = ():Promise<Array<ICertificate>> => {
        return getCertificates().then(data => {
            if (data.type !== ApiResponseType.OK) {
                return [];
            }
            // getCertificates guarantees an array payload on success.
            const payload:Array<ICertificate> = data.payload;

            payload.forEach((certificate:ICertificate) => {
                if (certificate.type === 'CFP') {
                    setHasCFP(true);
                }
                if (certificate.type === 'CFEP') {
                    setHasCFEP(true);
                }
                if (certificate.type === 'CGA') {
                    setHasCGA(true);
                }
                if (certificate.type === 'EFA') {
                    setHasEFA(true);
                }
                if (certificate.type === 'FPSB Emeritus') {
                    setHasEmeritus(true);
                }
            });

            if (hasEmeritus) {
                return payload.filter((cert:ICertificate)=>{return cert.type === 'FPSB Emeritus'});
            }
            return sortCertificateArray(payload);
        }).catch(() => []);
    }


    useEffect(() => {

        //Get List of Account Types from List Endpoint
        getList('client_account_type').then(res => {
            let optionListLabels:Array<string> = [];
            if (res) {

                //Collect private and Company Account Type to easier handle conditional display of fields
                res.forEach((item:any) => {
                    if (item.type === 'private') {
                        setClientAccountTypePrivate(item);
                    }
                    if (item.type === 'company') {
                        setClientAccountTypeCompany(item);
                    }
                    optionListLabels.push(item.label);
                });

                getList('client_account_payment_type').then(res => {
                    if (res) {
                        res.forEach((item:any) => {
                            if (item.type === 'direct_debit') {
                                let emptyClientClone = structuredClone(emptyClient);                                
                                emptyClientClone.client_account_payment_type = item.id;
                                setEmptyClient(emptyClientClone);
                                setDirectDebitPayment(item);
                            }
                            if (item.type === 'transfer') {
                                setTransferPayment(item);
                            }
                        })
                    }
                });

                

                //Fetch the Certificates of the user, if they are available to them
                loadCertificates().then(certificatesArray => {

                    //Move certs into state
                    setClientCertificates(certificatesArray);

                    // Fetch List of acutal Accounts of the Client
                    getClientAccounts().then(data => {
                        // getClientAccounts guarantees an array payload on success.
                        let accountsArray:Array<IClientAccount> = data.type === ApiResponseType.OK ? data.payload : [];
                        if (accountsArray && typeof accountsArray == 'object') {
                            if (accountsArray.length > 0) {
                                accountsArray.forEach((account:IClientAccount) => {
                                    // Parse Country into SelectOption from iso so it can be used in a controlled Input
                                    let isoCode = account.client_account_country;
                                    if (isoCode) {
                                        let isoValues = iso3166.filter(x => x.value === isoCode);
                                        if (isoValues) {
                                            account.client_account_country = isoValues[0];
                                        }
                                    }
        
                                    // Set Billing adress to be set by User (Maybe remove later if adress is synced from private or company data)
                                    account.client_account_own_billing_address = "1";

                                    const assignedCerts = certificatesArray.filter((cert:ICertificate) => {
                                        return cert.client_account_id === account.id;
                                    });
                                    account.assigned_certificates = assignedCerts.map((assignedCert:ICertificate):CertSimplified => ({
                                        id: assignedCert.id,
                                        type: assignedCert.type,
                                    }));

                                });
                                setAllClientAccounts(accountsArray);

                                //Create Toggle State of Tabs
                                let toggledStates = new Array(accountsArray.length).fill(false);
                                if (addition) {
                                    let activeIndex = accountsArray.findIndex(x => x.id === Number(addition));
                                    if (activeIndex >= 0) {
                                        toggledStates[activeIndex] = true;
                                    }
                                }
                                setToggledStates(toggledStates);
                            }
                        }
                        setIsLoading(false);

                    }).catch(() => handleApiError('Fehler beim Abruf der Rechnungskonten'));

                });
            }
        }).catch(() => handleApiError('Fehler beim Abruf der Einstellungen zu Rechnungskonten'));

        //Check if Form was changed and leve if needed
        const handleBeforeUnload = (e) => {
            if (unSavedChanges) {
                e.preventDefault();
                e.returnValue = "";
                return true;
            }
            return null;
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, []);

    //Detectes Changes inside a single ClientAccountForm and sets up leave intention check
    const outerIsChangedHandler = (value:boolean, closeForm:boolean = false) => {
        setUnSavedChanges(value);
        if (closeForm) {
            setShowNewFom(false);
        }
    }

    const shouldBlockNavigation = () => {
        if (unSavedChanges) {
            setNavigateConfirmModal(true);
            return "Sind Sie sicher, dass Sie die Seite verlassen wollen? Alle nicht gespeicherte Änderungen werden verloren gehen.";
        }
        return null;
    };

    //@ts-ignore
    let blocker = useBlocker(shouldBlockNavigation);
    const location = useLocation();
    const navigate = useNavigate();

    const blockedProceed = () => {
        if (location.pathname === blocker.location?.pathname) {
            navigate(0);
        } else {
            //@ts-ignore
            blocker.proceed();
        }
    }

    //A single Form was saved and it was a new Form, add it to the states
    const outerCreationHandler = (newClientAccount:IClientAccount, savedAccountId: number, savedSEPARefNumber:string, savedName:string, certificates:Array<CertSimplified>) => {


        let clientAccountsClone = structuredClone(allClientAccounts);
        newClientAccount.id = savedAccountId;
        newClientAccount.bank_account_sepa_mandate_reference = savedSEPARefNumber;
        newClientAccount.assigned_certificates = certificates;
        newClientAccount.name = savedName;
        if (newClientAccount.filename) {
            newClientAccount.is_sepa_document_uploaded = '1';
        } else {
            newClientAccount.is_sepa_document_uploaded = '0';
        }
        if (newClientAccount.client_account_type === clientAccountTypeCompany!.id) {
            newClientAccount.bank_account_company_debit_authorized = false;
        }


        let certificateIdsFlattend:Array<number> = [];
        certificates.forEach((c:CertSimplified) => {

            if (c.type === 'CFP' && hasEFA) {
                let efaCert = clientCertificates.filter((cert:ICertificate) => {
                    return cert.type === 'EFA';
                })[0];
                certificateIdsFlattend.push(efaCert.id);
            }

            if (c.type === 'CFEP' && hasCGA) {
                let cgaCert = clientCertificates.filter((cert:ICertificate) => {
                    return cert.type === 'CGA';
                })[0];
                certificateIdsFlattend.push(cgaCert.id);
            }

            certificateIdsFlattend.push(c.id);
        });

        if (certificates && certificates.length > 0) {
            newClientAccount.assigned_certificates = certificates;


            clientAccountsClone.forEach((accountInLoop:IClientAccount, key:number) => {
                let remainingCertsOfAccount = accountInLoop.assigned_certificates.filter((x) => {
                    return !certificateIdsFlattend.includes(x.id);
                });
                clientAccountsClone[key].assigned_certificates = remainingCertsOfAccount;
            });

            let certificatesToUpdate = clientCertificates.filter((cert:ICertificate) => {
                return certificateIdsFlattend.includes(cert.id) && cert.client_account_id !== savedAccountId;
            });
            certificatesToUpdate.forEach((cert:ICertificate) => {
                cert.client_account_id = savedAccountId;
                updateCertificate(cert);
            });
        }
        
        clientAccountsClone.push(newClientAccount);

        setAllClientAccounts(clientAccountsClone);
        setShowNewFom(false);
        setUnSavedChanges(false);
    }

    const clientAccountPreviewUpdateHandler = (account:IClientAccount) => {
        let accountsClone = structuredClone(allClientAccounts);
        let index = accountsClone.findIndex((loopAccount:IClientAccount) => {
            return loopAccount.id === account.id;
        })
        if (index) {
            accountsClone[index].is_sepa_document_uploaded = '1';
        }
        setAllClientAccounts(accountsClone);
    }

    //Visually open/close a single ClientAccountForm
    const toggleAccount = (event: Event, key:number) => {
        if (event.target && event.target.closest('button')) {
            return;
        }
        let toggledStatesClone = Array(toggledStates.length).fill(false);
        if (!toggledStates[key]) {
            toggledStatesClone[key] = true;
            let repeaterHeadline = document.querySelector('.form-group.account-item:nth-child('+(key+1)+')');
            setSelectedAccountType(undefined);
            if (repeaterHeadline) {
                setTimeout(() => {
                    var headerOffset = 25;
                    var elementPosition = repeaterHeadline.getBoundingClientRect().top;
                    var offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                    window.scrollTo({
                         top: offsetPosition,
                         behavior: "smooth"
                    });
                }, 0);
            }
        }
        setToggledStates(toggledStatesClone);
    }

    const certificateSelectHandler = (certificate:ICertificate, account:IClientAccount) => {
        setCertIsLoading(true);
        let clientCertificatesClone = structuredClone(clientCertificates);
        let accountsClone = structuredClone(allClientAccounts);

        let certificateIndex = clientCertificatesClone.findIndex(x => x.id === certificate.id);
        clientCertificatesClone[certificateIndex].client_account_id = account.id;
        certificate.client_account_id = account.id;

        let certsByAccountsWithUnsavedChangesClone = structuredClone(certsByAccountsWithUnsavedChanges);
        moveItem(certsByAccountsWithUnsavedChangesClone, certificate, account.id);
        setCertsByAccountsWithUnsavedChanges(certsByAccountsWithUnsavedChangesClone);
        
        if (certificate.type === 'CFP' && hasEFA) {
            let efaCert = clientCertificates.filter((cert:ICertificate) => {
                return cert.type === 'EFA';
            })[0];
            let efaCertCertificateIndex = clientCertificatesClone.findIndex(x => x.id === efaCert.id);
            clientCertificatesClone[efaCertCertificateIndex].client_account_id = account.id;
        }
        if (certificate.type === 'CFEP' && hasCGA) {
            let cgaCert = clientCertificates.filter((cert:ICertificate) => {
                return cert.type === 'CGA';
            })[0];
            let cgaCertCertificateIndex = clientCertificatesClone.findIndex(x => x.id === cgaCert.id);
            clientCertificatesClone[cgaCertCertificateIndex].client_account_id = account.id;
        }

        setClientCertificates(clientCertificatesClone);

        accountsClone.forEach((accountInLoop:IClientAccount, key:number) => {
            let connectedCertificateIndexInAccount = accountInLoop.assigned_certificates.findIndex(x => x.id === certificate.id);
            
            if (account.id === accountInLoop.id) {
                accountsClone[key].assigned_certificates.push({
                    id: certificate.id,
                    type: certificate.type
                });
            }

            if (connectedCertificateIndexInAccount !== -1) {
                accountsClone[key].assigned_certificates.splice(connectedCertificateIndexInAccount, 1);
            }

            accountsClone[key].assigned_certificates = sortCertificateArray(accountsClone[key].assigned_certificates);

        });

        setAllClientAccounts(accountsClone);

        setUnSavedChanges(true);
    }

    const saveAccount = (account:IClientAccount) => {
        let accountsBeingSavedClone = structuredClone(accountsBeingSaved);
        if (accountsBeingSavedClone.includes(account.id!)) {
            return;
        }
        accountsBeingSavedClone.push(account.id!);
        setAccountsBeingSaved(accountsBeingSavedClone);
        let certsByAccountsWithUnsavedChangesClone = structuredClone(certsByAccountsWithUnsavedChanges);
        let certsOfAccount = certsByAccountsWithUnsavedChangesClone[account.id];
        if (certsOfAccount) {
            let unResolvedCalls:Array<string> = [];
            certsOfAccount.forEach((certificate:ICertificate) => {
                unResolvedCalls.push(certificate.type);
            });
            certsOfAccount.forEach((certificate:ICertificate) => {
                if (certificate.type === 'CFP' && hasEFA) {
                    unResolvedCalls.push('efa');
                    let efaCert = clientCertificates.filter((cert:ICertificate) => {
                        return cert.type === 'EFA';
                    })[0];
                    updateCertificate(efaCert).then(() => {                        
                        unResolvedCalls.splice(unResolvedCalls.indexOf(efaCert.type),1);
                        if (unResolvedCalls.length <= 0) {
                            resolveSaveOfAccounts(certsByAccountsWithUnsavedChangesClone, account, accountsBeingSavedClone);
                        }
                    })
                }
                if (certificate.type === 'CFEP' && hasCGA) {
                    unResolvedCalls.push('cga');
                    let cgaCert = clientCertificates.filter((cert:ICertificate) => {
                        return cert.type === 'CGA';
                    })[0];
                    updateCertificate(cgaCert).then(() => {                        
                        unResolvedCalls.splice(unResolvedCalls.indexOf(cgaCert.type),1);
                        if (unResolvedCalls.length <= 0) {
                            resolveSaveOfAccounts(certsByAccountsWithUnsavedChangesClone, account, accountsBeingSavedClone);
                        }
                    })
                }
                updateCertificate(certificate).then(() => {
                    unResolvedCalls.splice(unResolvedCalls.indexOf(certificate.type),1);
                    if (unResolvedCalls.length <= 0) {
                        resolveSaveOfAccounts(certsByAccountsWithUnsavedChangesClone, account, accountsBeingSavedClone);
                    }
                });                
            });
        }
    }

    const resolveSaveOfAccounts = (certsByAccountsWithUnsavedChangesClone:Array<any>, account:IClientAccount, accountsBeingSavedClone:Array<any>) => {
        setResponseMessage('');
        setResponseMessageClass('');
        setTimeout(() => {
            setCertIsLoading(false);
            setResponseMessage('Erfolgreich gespeichert');
            setResponseMessageClass('success show');

            delete certsByAccountsWithUnsavedChangesClone[account.id];
            accountsBeingSavedClone.splice(accountsBeingSavedClone.indexOf(account.id), 1);
            setCertsByAccountsWithUnsavedChanges(certsByAccountsWithUnsavedChangesClone);
            if (Object.keys(certsByAccountsWithUnsavedChangesClone).length <= 0) {
                setUnSavedChanges(false);
            }

            setTimeout(() => {
                setResponseMessage('');
            }, 5000);
        }, 1);
    }

    const handleAccountTypeChange = (account:IClientAccount, key: number) => {        
        let postedClientAccount = structuredClone(account);
        postedClientAccount.client_account_type = selectedAccountType;

        updateClientAccount(postedClientAccount).then(res => {
            if (res.type === ApiResponseType.OK) {
                let allClientAccountsClone = structuredClone(allClientAccounts);
                allClientAccountsClone[key].client_account_type = selectedAccountType;
                allClientAccountsClone[key].name = res.payload.name;
                setAllClientAccounts(allClientAccountsClone);
                setSelectedAccountType(undefined);
                if (res.payload.success)  {
                    setResponseMessage('Erfolgreich gespeichert');
                    setResponseMessageClass('success show');
                    setTimeout(() => {
                        setResponseMessage('');
                    }, 5000);
                }
            } else {
                if (res.payload.errors!.general_error) {
                    setResponseMessage('Fehler. Bitte korrigieren Sie den Fehler und versuchen Sie es erneut.');
                    setResponseMessageClass('error show');
                }
            }
        })
    }
    
    return (
        <div className="single-tab-item">
            { isLoading ? (
                <div className="loader"></div>
            ) : (
                <ApiCheck errors={apiErrors ? apiErrors : null}>
                    { needsInitialReview ? (
                        <div className="intro">
                            <p>Bitte überprüfen Sie Ihre Rechnungsdaten.</p>
                            <p>Bestehende Rechnungskonten sind nach erfolgter Rechnungsstellung aufgrund geltender Aufbewahrungsfristen nicht änderbar - bitte legen Sie bei vorzunehmenden Änderungen neue Rechnungskonten an. Handelt es sich nur um Tippfehler, senden Sie bitte eine E-Mail an <a href="mailto:info@fpsb.de">info@fpsb.de</a>.</p>
                            <p>Wir versenden unsere Rechnungen im Format ZUGFeRD Extended, d. h. als maschinenlesbare E-Rechnung in Kombination mit einer PDF-Datei.</p>
                            <hr />
                            <h5>Bestehende Rechnungskonten</h5>
                        </div>
                    ) : (
                        <div className="intro">
                            <p>Bitte überprüfen Sie Ihre Rechnungsdaten.</p>
                            <p>Wenn Sie mehrere Zertifikate besitzen, können Sie individuell für jedes Zertifikat ein unterschiedliches Rechnungskonto anlegen. Bei bestehenden Rechnungskonten können Sie auswählen, welches Zertifikat über welches Rechnungskonto abgerechnet wird.</p>
                            <p>Bestehende Rechnungskonten sind aufgrund geltender Aufbewahrungsfristen nicht änderbar - bitte legen Sie bei vorzunehmenden Änderungen neue Rechnungskonten an. Handelt es sich nur um Tippfehler, senden Sie bitte eine E-Mail an <a href="mailto:info@fpsb.de">info@fpsb.de</a>.</p>
                            <p>Sie können für jedes Rechnungskonto angeben, ob die Zahlung per Überweisung oder über ein SEPA-Lastschriftmandat erfolgen soll.</p>
                            <p>Sollte ein SEPA-Lastschriftmandat für ein geschäftliches Rechnungskonto erteilt werden, ist eine gesonderte Einzugsermächtigung auszufüllen und hochzuladen.</p>
                            <p>Wir versenden unsere Rechnungen im Format ZUGFeRD Extended, d. h. als maschinenlesbare E-Rechnung in Kombination mit einer PDF-Datei.</p>
                            <hr />
                            <h5>Bestehende Rechnungskonten</h5>
                            <p>Hier finden Sie eine Übersicht über die bestehenden Rechnungskonten mit den zugehörigen Zertifikaten und die Information, ob es sich um ein privates oder ein geschäftliches Rechnungskonto handelt und ob die Zahlung per Überweisung oder via SEPA-Lastschriftmandat erfolgt.</p>
                        </div>
                    ) }

                    <div className="client-accounts-wrapper repeater">
                        { allClientAccounts.length > 0 ? (
                            <>
                                { allClientAccounts.map((account:IClientAccount, key:number) => (
                                    <div className={`form-group account-item single-repeater-item${(account.assigned_certificates && account.assigned_certificates.length > 0) ? ' has-certs' : ''}${!account.client_account_type ? ' no-type' : ''}`} key={'account-' + key} id={`account-${account.id}`}>
                                        <div className="account-details">
                                            <div className="repeater-toggle-headline" onClick={(e) => toggleAccount(e, key)}>
                                                <div className="name-with-img">
                                                    <span className="h6">{account.name}</span>
                                                    {(account.assigned_certificates && account.assigned_certificates.length > 0) && (
                                                        account.assigned_certificates.map((cert: CertSimplified, certkey: number) => (
                                                            <Fragment key={certkey}>
                                                                { hasEmeritus ? (
                                                                    cert.type === 'FPSB Emeritus' && (
                                                                        <img src={require(`../../../img/files/certificates/fpsbemeritus.jpg`)} key={'img_emeritus' + key + certkey + '_alt'} alt={`Zertifikat FPSB Emeritus`} />
                                                                    )
                                                                ) : (
                                                                    <>
                                                                        { cert.type === 'CFP' ? (
                                                                            <>
                                                                                <img src={require(`../../../img/files/certificates/cfp.jpg`)} key={'img_cfp' + key + certkey + '_alt'} alt={`Zertifikat CFP`} />
                                                                                {
                                                                                    hasCFP && hasEFA ? (
                                                                                        <img src={require(`../../../img/files/certificates/efa.jpg`)} key={'img_efa' + key + certkey + '_alt'} alt={`Zertifikat EFA`} />                                                                            
                                                                                    ) : ''
                                                                                }
                                                                            </>
                                                                        ) : ''}
                                                                        { cert.type === 'EFA' && !hasCFP ? (
                                                                            <img src={require(`../../../img/files/certificates/efa.jpg`)} key={'img_efa' + key + certkey + '_alt'} alt={`Zertifikat EFA`} />
                                                                        ) : ''}
                                                                        { cert.type === 'CFEP' ? (
                                                                            <>
                                                                                <img src={require(`../../../img/files/certificates/cfep.jpg`)} key={'img_cfep' + key + certkey + '_alt'} alt={`Zertifikat CFEP`} />
                                                                                {
                                                                                    hasCFEP && hasCGA ? (
                                                                                        <img src={require(`../../../img/files/certificates/cga.jpg`)} key={'img_cga' + key + certkey + '_alt'} alt={`Zertifikat CGA`} />                                                                            
                                                                                    ) : ''
                                                                                }
                                                                            </>
                                                                        ) : ''}
                                                                        { cert.type === 'CGA' && !hasCFEP ? (
                                                                            <img src={require(`../../../img/files/certificates/cga.jpg`)} key={'img_cga' + key + certkey + '_alt'} alt={`Zertifikat CGA`} />
                                                                        ) : ''}
                                                                        { cert.type === 'DIN-ISO 22222' ? (
                                                                            <img src={require(`../../../img/files/certificates/din-iso22222.jpg`)} key={'img_din_iso' + key + certkey + '_alt'} alt={`Zertifikat DIN ISO 22222`} />
                                                                        ) : ''}
                                                                    </>
                                                                ) }
                                                            </Fragment>
                                                        ))
                                                    )}
                                                    {
                                                        account.client_account_type === clientAccountTypeCompany.id && account.client_account_payment_type === directDebitPayment.id && !account.bank_account_company_debit_authorized && account.is_sepa_document_uploaded !== '1' ? (
                                                            <span className="error-inline">Für dieses Konto wurde noch kein SEPA-Mandat hochgeladen.</span>
                                                        ) : ''
                                                    }
                                                    {
                                                        account.client_account_type === clientAccountTypeCompany.id && !account.bank_account_company_debit_authorized && account.is_sepa_document_uploaded === '1' ? (
                                                            <span className="warning-inline">Das SEPA-Lastschriftmandat wird von der FPSB Geschäftsstelle geprüft</span>
                                                        ) : ''
                                                    }
                                                </div>
                                                { certsByAccountsWithUnsavedChanges[account.id] && !toggledStates[key] && <Button wrapperClassName="nowrap ml-auto" className={`button-small icon-${accountsBeingSaved.includes(account.id!) ? 'loading' : 'check'}`} type={'button'} buttonType='primary' onClick={() => saveAccount(account)}>Änderungen speichern</Button> }
                                                <ToggleIcon toggled={toggledStates[key]} width={15} height={15}/>
                                            </div>
                                            {toggledStates[key] && (
                                                <>
                                                    <div className="single-account-wrapper">
                                                        {clientCertificates.length > 0 && (<>
                                                        <p>Dieses Rechnungskonto zur Abrechnung von Zertifikaten auswählen</p>
                                                        <div className="controls-row row">
                                                            {clientCertificates.map((certificate: ICertificate, key: number) => {
                                                                let isSelected = account.assigned_certificates && account.assigned_certificates.filter(i => { return i.id === certificate.id; }).length > 0;
                                                                let isInActive = (certificate.type === 'EFA' && hasCFP) || (certificate.type === 'CGA' && hasCFEP);
                                                                if (!isBankingCertificate(certificate.type)) {
                                                                    return '';
                                                                }
                                                                return (
                                                                    <div className={`certificate-select-item${(isSelected  && !isInActive) ? ' selected' : ''}${isInActive ? ' inactive' : ''}`} key={'cert_item_' + key} onClick={() => {
                                                                        if (!isSelected && !isInActive) {
                                                                            certificateSelectHandler(certificate, account);
                                                                        }
                                                                    } }>
                                                                        <p>{certificate.type}</p>
                                                                        {certificate.type === 'EFA' && hasCFP && (
                                                                            <span className="tooltip">Bei gleichzeitiger CFP®-Zertifizierung fallen keine laufenden Lizenzgebühren an. Die Abrechnung mit 100% Rabatt erfolgt über das Rechnungskonto des CFP®-Zertifikats</span>
                                                                        )}
                                                                        {certificate.type === 'CGA' && hasCFEP && (
                                                                            <span className="tooltip">Bei gleichzeitiger CFEP®-Zertifizierung fallen keine laufenden Lizenzgebühren an. Die Abrechnung mit 100% Rabatt erfolgt über das Rechnungskonto des CFEP®-Zertifikats.</span>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                            { certsByAccountsWithUnsavedChanges[account.id] && <Button wrapperClassName="nowrap ml-auto" type={'button'} buttonType='primary' onClick={() => saveAccount(account)} className={accountsBeingSaved.includes(account.id!) ? 'icon-loading' : 'icon-check'}>Änderungen speichern</Button> }
                                                        </div>
                                                        </>)}
                                                        { responseMessage && (
                                                            <div className={`message${responseMessageClass ? ' ' + responseMessageClass : ''}`} role="alert" onClick={() => {setResponseMessage('');}}>
                                                                <span>
                                                                    {responseMessage}
                                                                </span>
                                                            </div>
                                                        ) }
                                                        <SingleAccountPreview
                                                            clientAccount={account}
                                                            clientAccountTypePrivate={clientAccountTypePrivate!}
                                                            clientAccountTypeCompany={clientAccountTypeCompany!}
                                                            directDebitPayment={directDebitPayment!}
                                                            transferPayment={transferPayment!}
                                                            outerUpdateHandler={(account:IClientAccount) => clientAccountPreviewUpdateHandler(account)}
                                                        />
                                                        { !account.client_account_type && (
                                                            <div className="client-account-type-overlay">
                                                                <div className="button-pills">
                                                                    <p>Bitte bestimmen Sie die Art des Rechnungskontos</p>
                                                                    <div className="button-wrapper">
                                                                        <Button
                                                                            buttonType='primary'
                                                                            type='button'
                                                                            className={selectedAccountType === clientAccountTypePrivate!.id ? '' : 'grey'}
                                                                            onClick={(e) => {
                                                                                e.preventDefault();
                                                                                setSelectedAccountType(clientAccountTypePrivate!.id);
                                                                            }}
                                                                        >{ clientAccountTypePrivate!.label }</Button>
                                                                        <Button
                                                                            buttonType='primary'
                                                                            type='button'
                                                                            className={selectedAccountType === clientAccountTypeCompany!.id ? '' : 'grey'}
                                                                            onClick={(e) => {
                                                                                e.preventDefault();
                                                                                setSelectedAccountType(clientAccountTypeCompany!.id);
                                                                            }}
                                                                        >{ clientAccountTypeCompany!.label }</Button>
                                                                    </div>
                                                                    {selectedAccountType ? (
                                                                        <Button
                                                                            buttonType='primary'
                                                                            type='button'
                                                                            onClick={() => {
                                                                                handleAccountTypeChange(account, key);
                                                                            }}
                                                                        >Speichern</Button>
                                                                    ) : ''}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )) }
                            </>
                        ) : (
                            <p>Für Sie sind noch keine Rechnugskonten vorhanden.</p>
                        )}
                    </div>

                    <Button wrapperClassName="my-2" type={'button'} buttonType='primary' onClick={() => setShowNewFom(!showNewForm)}>Neues Rechnungskonto erstellen</Button>
                    { showNewForm ? (
                        <>
                            <h5 id="scroll-to-headline">Neues Rechnungskonto anlegen</h5>
                            <p>Sie können für jedes Zertifikat ein eigenes Rechnungskonto anlegen und die Art der Zahlung festlegen.</p>
                            <ClientAccountForm
                                inputClientAccount={emptyClient}
                                clientAccountTypePrivate={clientAccountTypePrivate!}
                                clientAccountTypeCompany={clientAccountTypeCompany!}
                                transferPayment={transferPayment!}
                                directDebitPayment={directDebitPayment!}
                                outerIsChangedHandler={outerIsChangedHandler}
                                outerCreationHandler={(previous:IClientAccount, savedAccountId:number, savedSEPARefNumber:string, savedName:string, certificates:Array<ICertificate>) => outerCreationHandler(previous, savedAccountId, savedSEPARefNumber, savedName, certificates)}
                                outerIsLoading={certIsLoading}
                                profile={profile}
                                certificates={clientCertificates}
                                hasCFEP={hasCFEP}
                                hasCFP={hasCFP}
                                emptyClient={emptyClient}
                            />
                        </>
                    ) : ''}
                    { navigateConfirmModal && 
                        <NavigateConfirmModal 
                            proceed={() => blockedProceed()} 
                            setterCallback={setNavigateConfirmModal} 
                            text={<p className="align-center">Sind Sie sicher, dass Sie die Seite verlassen wollen? <br></br>Alle nicht gespeicherte Änderungen werden verloren gehen.</p>} 
                            leaveText={'ohne Speichern fortfahren'}
                        /> }
                    { responseMessage && (
                        <div className={`message${responseMessageClass ? ' ' + responseMessageClass : ''}`} role="alert" onClick={() => {setResponseMessage('');}}>
                            <span>
                                {responseMessage}
                            </span>
                        </div>
                    ) }
                </ApiCheck>
            )}
        </div>
    )
}

export default TabBankConnection;